import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { PpfCalculatorService, PpfResult, PPF_CONSTRAINTS } from './ppf-calculator.service';

@Component({
  selector: 'app-ppf-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './ppf-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ppf-calculator.scss']
})
export class PpfCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(PpfCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = PPF_CONSTRAINTS;
  readonly Math = Math;

  yearlyInvestment = 150_000;
  annualRate = 7.1;
  tenureYears = 15;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     PpfResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.yearlyInvestment, this.C.yearlyInvestment.min, this.C.yearlyInvestment.max, 'Yearly investment');
    if (e1) errors['yearlyInvestment'] = e1;
    const e2 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Interest rate');
    if (e2) errors['annualRate'] = e2;
    const e3 = validateRange(this.tenureYears, this.C.tenureYears.min, this.C.tenureYears.max, 'Tenure');
    if (e3) errors['tenureYears'] = e3;
    else if (!Number.isInteger(Number(this.tenureYears))) errors['tenureYears'] = 'Tenure must be a whole number of years.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'tenureYears') this.tenureYears = Math.floor(this.tenureYears || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { yearlyInvestment: true, annualRate: true, tenureYears: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.yearlyInvestment, this.annualRate, Math.floor(this.tenureYears));
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Invested', value: r.totalInvested, color: '#3b82f6' },
        { label: 'Interest Earned', value: r.totalInterest, color: '#10b981' }
      ], 'Maturity Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.rows.map(row => `Yr ${row.year}`),
        [{ label: 'Balance', data: r.rows.map(row => Math.round(row.closingBalance)), color: '#3b82f6' }]
      );
    }, 60);
  }

  reset(): void {
    this.yearlyInvestment = 150_000;
    this.annualRate       = 7.1;
    this.tenureYears      = 15;
    this.formErrors       = {};
    this.touched          = {};
    this.showFormErrors   = false;
    this.calculated        = false;
    this.result            = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
